import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";
import { BriefcaseBusiness, CalendarDays, CreditCard, FileClock, KeyRound, LifeBuoy, Mail, ShieldCheck, UserRound } from "lucide-react";
import { DataPanel } from "../components/CommandSystem";
import { hasPermission } from "../lib/permissions";

export function Profile() {
  const { user } = useAuth();
  const [advData, setAdvData] = useState(null);
  const roleLabel = (user?.role || "").replace(/_/g, " ");
  const canViewAuditLogs = hasPermission(user, "view_audit_logs");
  const initials = (user?.name || user?.email || "HX")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  useEffect(() => {
    if (user?.role === "advertiser_admin" || user?.role === "employee") {
      api.get("/auth/me/advertiser", { skipGlobalLoader: true })
        .then(({ data }) => setAdvData(data))
        .catch(() => {});
    }
  }, [user]);

  return (
    <Layout title="Account Settings" subtitle="Manage your profile, security, and subscription.">
      <div className="hx-profile-page">
        <div className="hx-profile-grid">
          <DataPanel className="hx-profile-account-panel">
            <div className="hx-profile-identity">
              <div className="hx-profile-avatar">{initials || "HX"}</div>
              <div className="min-w-0">
                <h3>{user?.name || "Account user"}</h3>
                <p>{user?.email || "No email available"}</p>
              </div>
              <span>{roleLabel || "user"}</span>
            </div>

            <div className="hx-profile-fields">
              <ProfileField icon={UserRound} label="Name" value={user?.name || ""} />
              <ProfileField icon={Mail} label="Email" value={user?.email || ""} />
              <ProfileField icon={BriefcaseBusiness} label="Role" value={roleLabel} />
              <ProfileField icon={CalendarDays} label="Joined" value={fmtDate(user?.created_at)} />
            </div>

            <ChangePassword />
          </DataPanel>

          <div className="hx-profile-side-stack">
          {canViewAuditLogs && (
            <div className="hx-card hx-profile-side-card hx-profile-help">
              <div className="hx-profile-side-head">
                <div><FileClock size={18} /></div>
                <h3>Audit Logs</h3>
              </div>
              <p>Review recent account activity and operational changes.</p>
              <Link to="/audit-logs">
                View Audit Logs
              </Link>
            </div>
          )}

          {advData && (
            <div className="hx-card hx-profile-side-card hx-profile-subscription">
              <div className="hx-profile-side-head">
                <div><CreditCard size={18} /></div>
                <h3>Subscription</h3>
              </div>

              <div className="hx-profile-kv">
                <span>Plan</span>
                <strong>{advData.advertiser.plan_type}</strong>
              </div>

              <div className="hx-profile-kv">
                <span>Days remaining</span>
                <strong className={
                  advData.days_remaining <= 0
                    ? "is-danger"
                    : advData.days_remaining <= 14
                    ? "is-warning"
                    : ""
                }>
                  {advData.days_remaining < 0
                    ? "No expiry set"
                    : advData.days_remaining === 0
                    ? "Expired"
                    : `${advData.days_remaining} days`}
                </strong>
              </div>

              {advData.advertiser.subscription_expiry && (
                <div className="hx-profile-kv">
                  <span>Expires on</span>
                  <strong>{fmtDate(advData.advertiser.subscription_expiry)}</strong>
                </div>
              )}

              <div className="hx-profile-usage">
                <div className="hx-profile-usage-row">
                  <span>Available hoarding days</span>
                  <strong>{Number(advData.advertiser.hoarding_days?.remaining_days || 0).toLocaleString("en-IN")}</strong>
                </div>
                <div className="hx-profile-usage-row">
                  <span>Hoardings used</span>
                  <strong>{advData.advertiser.hoardings_used} / {advData.advertiser.hoarding_limit}</strong>
                </div>
                <div className="hx-profile-progress">
                  <i
                    className={advData.advertiser.hoardings_used >= advData.advertiser.hoarding_limit ? "is-full" : ""}
                    style={{
                      width: `${Math.min(
                        100,
                        advData.advertiser.hoarding_limit > 0
                          ? (advData.advertiser.hoardings_used / advData.advertiser.hoarding_limit) * 100
                          : 0
                      )}%`,
                    }}
                  />
                </div>
                {advData.advertiser.hoardings_used >= advData.advertiser.hoarding_limit && (
                  <p>Hoarding limit reached. Contact sales to upgrade.</p>
                )}
              </div>
            </div>
          )}

          <div className="hx-card hx-profile-side-card hx-profile-help">
            <div className="hx-profile-side-head">
              <div><LifeBuoy size={18} /></div>
              <h3>Help</h3>
            </div>
            <p>Need more hoarding slots, a custom plan, or have questions?</p>
            <div className="hx-profile-help-actions">
              <a href="mailto:sales@hoardexa.com">
                sales@hoardexa.com
              </a>
              <a href="mailto:contact@hoardexa.com">
                contact@hoardexa.com
              </a>
              <a href="https://wa.me/919067611076" target="_blank" rel="noreferrer">
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </div>
      </div>
    </Layout>
  );
}

function ProfileField({ icon: Icon, label, value }) {
  return (
    <div className="hx-profile-field">
      <div><Icon size={16} /></div>
      <Label>{label}</Label>
      <Input value={value || ""} readOnly />
    </div>
  );
}

function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  async function applyChange() {
    if (!currentPassword || !newPassword) return toast.error("Enter current password and new password");
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password updated");
      setCurrentPassword("");
      setNewPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }

  return (
    <div className="hx-profile-password">
      <div className="hx-profile-password-head">
        <div><KeyRound size={16} /></div>
        <div>
          <h4>Change password</h4>
          <p>Use a strong password that is not shared anywhere else.</p>
        </div>
        <ShieldCheck size={18} />
      </div>
      <div className="hx-profile-password-grid">
        <div className="hx-profile-password-field">
          <Label>Current password</Label>
          <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current password" />
        </div>
        <div className="hx-profile-password-field">
          <Label>New password</Label>
          <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password" />
        </div>
      </div>
      <div className="hx-profile-actions">
        <Button type="button" onClick={applyChange}>
          Update password
        </Button>
      </div>
    </div>
  );
}
